/**
 * Upload the file that fills a missing document slot in the readiness
 * Documents ledger ("One row per fact": the dashed slot becomes the file).
 *
 * Pre-fills the doc type with the requirement's gate name when the taxonomy
 * has a docType of that exact name (Appraisal, Credit Report, …); otherwise the
 * closer picks the type. When the slot belongs to a borrower / property /
 * entity that can be resolved on the loan, the upload is associated with it,
 * the same way the entity sidebars upload.
 */
import { useCallback, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import BasicModal from "../modals/BasicModal";
import FileUploadProcess from "../processes/FileUploadProcess";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { Muted, Pill } from "./ReadinessBits";
import { TYPE } from "./typography";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { slate, steel } from "../../theme/colors";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Which loan object a missingDocuments row is about, resolved by label —
// the readiness payload carries labels, not ids.
export const associationFor = (obj, loan) => {
  if (!obj || !loan) return {};
  const label = norm(obj.objectLabel);
  if (!label) return {};
  if (obj.objectType === "borrower") {
    const b = (loan.borrowers || []).find(
      (x) => norm([x?.firstName, x?.lastName].filter(Boolean).join(" ")) === label,
    );
    return b?._id ? { borrowerIds: [b._id] } : {};
  }
  if (obj.objectType === "subjectProperty") {
    const p = (loan.subjectProperties || []).find((x) => {
      const full = addressLabel(x?.address) || addressLabel(x) || "";
      const n = norm(full);
      return n && (n === label || n.includes(label) || label.includes(n));
    });
    return p?._id ? { subjectPropertyIds: [p._id] } : {};
  }
  if (obj.objectType === "entity") {
    const e = (loan.entities || loan.borrowingEntities || []).find(
      (x) => norm(x?.entityName || x?.name) === label,
    );
    return e?._id ? { entityIds: [e._id] } : {};
  }
  return {};
};

const MissingDocUploadModal = ({ show, onClose, dealId, gate, obj, loan, onUploaded }) => {
  const { uploadLoanDocs, uploadLoanDocsWithAssociations } = useLoanFilesHook();
  const groupsAndTypes = useSelector((state) => state.fetchedSelections?.fileDocGroupAndTypes);
  const [busy, setBusy] = useState(false);

  // Gate name → { docGroup, docType } when the taxonomy has that exact type.
  const preset = useMemo(() => {
    if (!groupsAndTypes || typeof groupsAndTypes !== "object" || !gate) return null;
    const target = norm(gate);
    for (const [group, types] of Object.entries(groupsAndTypes)) {
      const hit = (Array.isArray(types) ? types : []).find((t) => norm(t) === target);
      if (hit) return { docGroup: group, docType: hit };
    }
    return null;
  }, [groupsAndTypes, gate]);

  const association = useMemo(() => associationFor(obj, loan), [obj, loan]);
  const hasAssociation = Object.keys(association).length > 0;

  const handleSubmit = useCallback(
    (files, { onSuccessFn } = {}) => {
      if (!dealId || !files?.length) return;
      setBusy(true);
      const done = () => {
        setBusy(false);
        onSuccessFn?.();
        onUploaded?.();
        onClose?.();
      };
      const fail = () => setBusy(false);
      if (hasAssociation) {
        uploadLoanDocsWithAssociations({
          loanId: dealId,
          files,
          ...association,
          onSuccessMsg: `${gate}: ${files.length} file${files.length === 1 ? "" : "s"} uploaded`,
          onSuccessFn: done,
          onFailFn: fail,
        });
      } else {
        uploadLoanDocs({
          loanId: dealId,
          files,
          onSuccessMsg: `${gate}: ${files.length} file${files.length === 1 ? "" : "s"} uploaded`,
          onSuccessFn: done,
          onFailFn: fail,
        });
      }
    },
    [dealId, gate, association, hasAssociation, uploadLoanDocs, uploadLoanDocsWithAssociations, onUploaded, onClose],
  );

  if (!show) return null;
  return (
    <BasicModal
      show={show}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      title={`Upload ${gate || "document"}`}
      description={
        <Flx ac wrap g={0.75} sx={{ mt: 0.5 }}>
          <Muted>Fills the</Muted>
          <Pill label={gate} c={steel.main} bg={steel.tint} size="xs" />
          <Muted>slot for</Muted>
          <Txt sx={{ ...TYPE.meta, color: slate[700], fontWeight: 600 }}>
            {obj?.objectType === "loan" ? "the loan file" : obj?.objectLabel}
          </Txt>
          {preset ? (
            <Muted>· filed as {preset.docType}</Muted>
          ) : (
            <Muted>· pick the document type below</Muted>
          )}
        </Flx>
      }
    >
      <FileUploadProcess
        key={`${gate}-${obj?.objectLabel}`}
        loanId={dealId}
        docType={preset?.docType}
        docGroup={preset?.docGroup}
        onSubmit={handleSubmit}
        loading={busy}
        borderSize={2}
        showDivider
        uploadText={`Drop the ${gate || "file"} here or click to browse`}
      />
    </BasicModal>
  );
};

export default MissingDocUploadModal;
